import { Fragment } from "react";

import { Heading, Page } from "@eisox/design-system";
import { createFileRoute, Outlet } from "@tanstack/react-router";

import { getSettingsNavigation, Navlink } from "~/features/settings";
import { requirePagePermission } from "~/helpers/auth/route-permission.helper";
import { buildingPageHead } from "~/helpers/document-title.helper";
import { m } from "~/paraglide/messages";
import { useBuildingContext } from "~/providers/building.provider";

export const Route = createFileRoute("/_authenticated/buildings_/$buildingId/settings")({
  beforeLoad: ({ context: { permissions }, params: { buildingId }, preload }) => {
    requirePagePermission(permissions, "settings", buildingId, preload);
  },
  head: ({ match: { context }, params: { buildingId } }) =>
    buildingPageHead(context.queryClient, buildingId, m.settings()),
  component: RouteComponent,
});

function RouteComponent() {
  const { buildingId } = Route.useParams();

  const { boilerRooms, airHandlerUnits, permissions } = useBuildingContext(Route.id);

  // Shared with the command palette, so the two can never offer a different set of settings pages.
  const sections = getSettingsNavigation({
    permissions,
    hasBoilerRooms: boilerRooms.length > 0,
    hasAirHandlerUnits: airHandlerUnits.length > 0,
  });

  return (
    <Page.Root>
      <Page.Header>
        <Page.Title>{m.settings()}</Page.Title>
      </Page.Header>
      <Page.Content className="flex">
        <div className="gap-2.5 p-10 flex flex-col">
          {sections.map(section => (
            // A fragment rather than a wrapper: the column's own gap keeps headings and links evenly spaced.
            <Fragment key={section.id}>
              <Heading as="h2" size={3}>
                {section.title}
              </Heading>
              {section.links.map(link => (
                <Navlink key={link.id} to={link.to} params={{ buildingId }}>
                  {link.label}
                </Navlink>
              ))}
            </Fragment>
          ))}
        </div>
        <div className="p-10 flex-1">
          <Outlet />
        </div>
      </Page.Content>
    </Page.Root>
  );
}
